import React, {useEffect, useRef} from 'react';

import {type VideoStreamingPlayerProps} from './types';
import {useInitializeVideoPlayer} from './useInitializeVideoPlayer';

// 16:9 box used for a video tile in "Original size" mode before it has decoded
const LOADING_PLACEHOLDER_DIMENSIONS = {width: 320, height: 180};

export const StreamingVideoViewer = ({
  hlsSrc,
  downloadUrl,
  setHasFailedToLoadStream,
  trackSuccess,
  trackError,
  headerElement,
  footerElement,
  autoPlay,
  autoLoop,
  refCallback,
  mimeType,
  videoPlayerTimeStep,
  sizeToIntrinsic,
}: VideoStreamingPlayerProps) => {
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const {videoRef, playerRef, decodedDimensions} = useInitializeVideoPlayer({
    hlsSrc,
    downloadUrl,
    trackSuccess,
    trackError,
    setHasFailedToLoadStream,
    autoPlay,
    autoLoop,
    mimeType,
    videoPlayerTimeStep,
  });
  const videoBox = decodedDimensions ?? LOADING_PLACEHOLDER_DIMENSIONS;
  const shouldUseIntrinsicBox = sizeToIntrinsic === true;

  useEffect(() => {
    if (refCallback) {
      refCallback(videoRef.current);
    }
  }, [refCallback, videoRef]);

  // Keep the player sized to its container as the panel resizes.
  useEffect(() => {
    const playerContainer = playerContainerRef.current;
    if (playerContainer == null || typeof ResizeObserver === 'undefined') {
      return;
    }
    const resizeObserver = new ResizeObserver(([entry]) => {
      const {width, height} = entry.contentRect;
      const player = playerRef.current;
      if (player == null || width === 0 || height === 0) {
        return;
      }
      player.dimension('width', width);
      player.dimension('height', height);
      player.trigger('playerresize');
    });
    resizeObserver.observe(playerContainer);
    return () => resizeObserver.disconnect();
  }, [playerRef]);

  // Intrinsic mode: the outer wrapper shrinks to content (header + video + footer)
  // and the video area carries the decoded height, so the cell sizes to the video
  // rather than the video being squeezed into a chrome-reduced cell. Only the
  // runtime pixel dimensions stay inline (Tailwind can't express dynamic values);
  // the static layout bits live in the class lists.
  const outerConditionalClassName = shouldUseIntrinsicBox
    ? 'flex-none h-auto max-w-none'
    : 'h-full w-full max-w-full';
  const outerStyle: React.CSSProperties | undefined = shouldUseIntrinsicBox
    ? {width: videoBox.width}
    : undefined;
  const videoAreaConditionalClassName = shouldUseIntrinsicBox
    ? 'flex-none'
    : 'flex-auto';
  const videoAreaStyle: React.CSSProperties | undefined = shouldUseIntrinsicBox
    ? {height: videoBox.height}
    : undefined;

  return (
    <div
      className={`flex flex-col overflow-hidden ${outerConditionalClassName}`}
      style={outerStyle}>
      <style>
        {`
        .vjs-fullscreen video {
          filter: none;
        }

        .video-js {
          max-width: 100%;
          max-height: 100%;

          .vjs-current-time,
          .vjs-time-divider,
          .vjs-duration {
            display: flex;
            padding-left: 1px;
            padding-right: 1px;
            min-width: 6px;
          }
        }

        .video-js.vjs-fill {
          width: 100%;
          height: 100%;
        }

        .video-js .vjs-tech {
          object-fit: contain;
        }

        .vjs-download-button .vjs-icon-placeholder:before {
          content: none;
        }

        .vjs-download-button--loading {
          cursor: default;
          pointer-events: none;
        }

        .vjs-download-button__icon-container svg {
          display: block;
        }

        .vjs-download-button--loading .vjs-download-button__icon-container svg {
          animation: vjs-download-button-spin 1s linear infinite;
        }

        @keyframes vjs-download-button-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .vjs-layout-x-small .vjs-control-bar {
          .vjs-download-button {
            display: none;
          }
        }


        .vjs-layout-tiny .vjs-control-bar {
          .vjs-play-control {
            font-size: 8px;
          }
          .vjs-progress-control {
            display: none;
          }
          .vjs-volume-panel {
            display: none;
          }
          .vjs-playback-rate-control {
            display: none;
          }
          .vjs-download-button {
            display: none;
          }
          .vjs-fullscreen-control {
            font-size: 8px;
          }
          .vjs-control {
            width: 24px;
          }
        }
      `}
      </style>
      {headerElement && <div className="flex-none">{headerElement}</div>}
      <div
        className={`min-h-0 w-full overflow-hidden ${videoAreaConditionalClassName}`}
        style={videoAreaStyle}>
        <div
          ref={playerContainerRef}
          data-vjs-player
          data-is-hls={hlsSrc != null}
          className="h-full max-h-full w-full max-w-full overflow-hidden">
          <video ref={videoRef} className="video-js vjs-big-play-centered">
            <track kind="captions" />
          </video>
        </div>
      </div>
      {footerElement && <div className="flex-none">{footerElement}</div>}
    </div>
  );
};
